import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Phone, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import { BRAND, PLATFORMS } from "./data";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [platforms, setPlatforms] = useState(["iOS"]);

  const toggle = (p) =>
    setPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));

  const estimate = useMemo(() => {
    const weeks = 3 + platforms.length * 2;
    return { weeks, range: `${weeks}–${weeks + 3} weeks` };
  }, [platforms]);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and a short message.");
      return;
    }
    toast.success("Transmission received! Charles will reply within 24 hours.");
    setForm({ name: "", email: "", phone: "", message: "" });
    setPlatforms(["iOS"]);
  };

  const input =
    "w-full px-4 py-3 rounded-xl chip txt-primary placeholder:opacity-50 focus:outline-none focus:border-purple-400/50 focus:ring-1 focus:ring-purple-400/30 transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 px-5">
      <div className="absolute inset-0 ambient-glow" />
      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
        >
          <p className="font-mono text-xs tracking-[0.3em] uppercase overline mb-4">
            / Start a conversation
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight txt-primary leading-[1.05]">
            Let's build <span className="text-gradient">something great.</span>
          </h2>
          <p className="mt-5 text-lg txt-soft max-w-md">
            Tell us about your idea and we'll get back within 24 hours. Prefer to
            talk? Call the founder directly.
          </p>

          <a
            data-testid="contact-call-button"
            href={`tel:${BRAND.phoneTel}`}
            className="mt-8 inline-flex items-center gap-3 px-6 py-4 rounded-2xl glass hover:border-purple-400/40 transition-colors"
          >
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600">
              <Phone className="w-5 h-5 text-white" />
            </span>
            <div>
              <p className="text-xs txt-dim">Call Charles Rieder</p>
              <p className="font-mono text-lg txt-primary">{BRAND.phoneDisplay}</p>
            </div>
          </a>

          <div className="mt-6 flex items-center gap-2 text-sm txt-soft">
            <Clock className="w-4 h-4 text-emerald-500" />
            Mon–Fri · Eastern Time · {BRAND.location}
          </div>

          <div className="mt-10 p-6 rounded-2xl glass" data-testid="scope-estimator">
            <p className="font-mono text-xs tracking-[0.2em] uppercase overline mb-4">
              Quick scope estimate
            </p>
            <div className="flex flex-wrap gap-2">
              {PLATFORMS.map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`estimator-platform-${p.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => toggle(p)}
                  className={`px-3.5 py-1.5 rounded-full text-sm border transition-all ${
                    platforms.includes(p)
                      ? "border-purple-400/60 bg-purple-500/15 txt-primary"
                      : "chip hover:border-purple-400/40"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
            <div className="mt-5 flex items-baseline gap-3">
              <span className="txt-soft text-sm">Estimated timeline</span>
              <span className="font-heading text-2xl font-bold text-gradient" data-testid="estimator-result">
                {platforms.length ? estimate.range : "—"}
              </span>
            </div>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          onSubmit={submit}
          data-testid="contact-form"
          className="p-7 sm:p-9 rounded-3xl glass"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm txt-soft mb-1.5 block">Name</label>
              <input data-testid="contact-form-name-input" className={input} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="text-sm txt-soft mb-1.5 block">Phone</label>
              <input data-testid="contact-form-phone-input" className={input} placeholder="Optional" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-sm txt-soft mb-1.5 block">Email</label>
            <input data-testid="contact-form-email-input" className={input} placeholder="you@company.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="mt-4">
            <label className="text-sm txt-soft mb-1.5 block">Your app idea</label>
            <textarea data-testid="contact-form-message-input" rows={5} className={`${input} resize-none`} placeholder="Tell us what you want to build…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          </div>
          <button
            type="submit"
            data-testid="contact-form-submit-button"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl font-semibold text-white bg-gradient-to-r from-sky-500 via-indigo-600 to-purple-600 hover:shadow-[0_0_35px_rgba(99,102,241,0.5)] transition-shadow"
          >
            <Send className="w-4 h-4" />
            Send transmission
          </button>
          <p className="mt-3 text-center text-xs txt-dim">
            Guaranteed reply within 24 hours by Charles Rieder.
          </p>
        </motion.form>
      </div>
    </section>
  );
};
